<template>
    <v-container fluid>
        <v-card>
            <v-overlay :value="loading" absolute>
                <div class="text-center">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        :size="50"
                        class="my-4"
                    ></v-progress-circular>
                    <p class="display-1">{{ $t("Loading") }}</p>
                </div>
        </v-overlay>
        <template v-if="!loading">
            <v-card-title align-center class="py-3">{{ pageTitle }}</v-card-title>
            <v-card-text class="px-5">
                <div class="field-rights-config_header-buttons">
                    <v-menu bottom right>
                        <template v-slot:activator="{ on: menu, attrs }">
                            <v-tooltip top>
                                <template v-slot:activator="{ on: tooltip }">
                                    <v-btn text small v-bind="attrs" v-on="{ ...tooltip, ...menu }" color="blue-grey">
                                        <v-icon large>mdi-history</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t("Versions") }}</span>
                            </v-tooltip>
                        </template>
                        <v-list>
                            <v-list-item v-for="(item, i) in versionList" :key="i" @click="onVersionChange(item)">
                                <v-list-item-title>{{ `v${item.text}` }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-btn color="secundary" @click="close">{{ $t("Cancel") }}</v-btn>
                    <v-btn color="primary" @click="saveContentTypeFieldsRights">{{ $t("Save") }}</v-btn>
                </div>
                <div>
                <v-row v-if="fieldsRightsItems.length == 0">
                <v-col cols="1" align-self="center">
                    <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                        v-on="on"
                        large
                        color="primary"
                        icon
                        @click="addContentTypeFieldsRights()"
                        >
                        <v-icon>mdi-plus</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t("Add") }}</span>
                    </v-tooltip>
                </v-col>
                </v-row>
                <div v-for="(item, index) in fieldsRightsItems" :key="item.uuid">
                <v-row>
                    <v-col align-self="center" cols="1">
                    <v-tooltip top>
                        <template v-slot:activator="{ on }">
                        <v-btn
                            v-on="on"
                            large
                            color="error"
                            icon
                            @click="removeContentTypeFieldsRights(item.uuid)"
                        >
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                        </template>
                        <span>{{ $t("Delete") }}</span>
                    </v-tooltip>
                    </v-col>
                    <v-col align-self="center" cols="2">
                    <v-text-field
                        readonly
                        v-model="item.displayConditionStr"
                        :label="$t('Condition')"
                        :append-icon="
                        item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'
                        "
                        @click:append="editCondition(item)"
                    ></v-text-field>
                    </v-col>
                    <v-col align-self="center" cols="2">
                    <v-select
                        :label="$t('Role')"
                        v-model="item.roleTypeID"
                        :items="rolesList"
                        item-text="text"
                        item-value="id"
                        :error-messages="roleErrors(index)"
                    ></v-select>
                    </v-col>
                    <v-col cols="2" align-self="center">
                    <v-select
                        :label="$t('Field')"
                        v-model="item.defFieldID"
                        :items="contentType.fields"
                        item-text="name"
                        item-value="id"
                        :error-messages="fieldErrors(index)"
                    ></v-select>
                    </v-col>
                    <v-col cols="2" align-self="center">
                    <v-tooltip top :disabled="!fieldRequired(item)">
                        <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" style="width: max-content; margin-left: 50px;">
                            <v-switch
                            v-on="on"
                            v-model="item.visible"
                            :disabled="fieldRequired(item)"
                            :label="$t('Visible')"
                            ></v-switch>
                        </div>
                        </template>
                        <span>{{ $t("isRequired") }}</span>
                    </v-tooltip>
                    </v-col>
                    <v-col cols="2" align-self="center">
                    <v-switch v-model="item.readonly" style="width: max-content; margin-left: 20px;">
                        <template v-slot:label>
                        <span style="white-space: nowrap">{{
                            $t("Readonly")
                        }}</span>
                        </template>
                    </v-switch>
                    </v-col>
                    <v-col cols="1" align-self="center">
                    <v-tooltip top>
                        <template v-slot:activator="{ on }">
                        <v-btn
                            v-on="on"
                            large
                            color="primary"
                            icon
                            @click="addContentTypeFieldsRights()"
                        >
                            <v-icon>mdi-plus</v-icon>
                        </v-btn>
                        </template>
                        <span>{{ $t("Add") }}</span>
                    </v-tooltip>
                    </v-col>
                </v-row>
                </div>
            </div>
            </v-card-text>
        </template>
        <SearchCriteriaBuilderModal
            :ref="conditionModalKey"
            :contentTypeIDs="[contentType.id]"
            @on-condition-saved="onConditionChanged"
            :allowEmptyCondition="true"
            :onlyShowVisibleFields="false"
        />
        </v-card>
    </v-container>
</template>
<script>
import SearchCriteriaBuilderModal from '@/components/search/SearchCriteriaBuilderModal.vue';
import ContentType from '@/ecm/models/contentType/ContentType';
import ContentTypeField from '@/ecm/models/contentType/ContentTypeField';
import ContentTypeFieldRight from '@/ecm/models/contentType/ContentTypeFieldRight';
import ContentTypeService from '@/services/ContentTypeService';
import RoleService from '@/services/RoleService';
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

export default {
    name: 'AccessRightsFields',
    components: {
        SearchCriteriaBuilderModal,
    },
    data() {
        return {
            loading: false,
            contentType: new ContentType(),
            conditionModalOpen: false,
            fieldsRightsItems: [],
            currentItem: new ContentTypeFieldRight(),
            versionList: [],
            rolesList: [],
            accessFieldsRightsList: [],
            digitalSignaturesList: [],
            conditionModalKey: 'conditionModalKey',
        };
    },
    mixins: [validationMixin],
    validations: {
        fieldsRightsItems: {
            $each: {
                roleTypeID: { required },
                defFieldID: { required },
            },
        },
    },
    mounted() {
        this.loading = true;
        this.getRoles();
        this.loadContentData();
    },
    computed: {
        pageTitle() {
            return `${this.$t('FieldsAccessRights')} - ${this.contentType.name} ${this.contentType.version}`;
        },
        conditionModalInstance() {
            return this.$refs[this.conditionModalKey];
        },
    },
    methods: {
        loadContentData() {
            const id = this.$route.params.id;
            if (id) {
                ContentTypeService.get(id)
                    .then((data) => {
                        this.edit(data);
                    })
                    .catch((error) => {
                        console.error('Error loading data:', error);
                    });
            }
            this.loading = false;
        },
        roleErrors(index) {
            const errors = [];
            if (!this.$v.fieldsRightsItems.$each[index].roleTypeID.$dirty)
                return errors;
            !this.$v.fieldsRightsItems.$each[index].roleTypeID.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        fieldRequired(item) {
            const field = this.contentType.fields.find((f) => f.id === item.defFieldID);
            return field ? field.required : false;
        },
        fieldErrors(index) {
            const errors = [];
            if (!this.$v.fieldsRightsItems.$each[index].defFieldID.$dirty)
                return errors;
            !this.$v.fieldsRightsItems.$each[index].defFieldID.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        edit(dto) {
            this.contentType = new ContentType(dto);
            this.getContentTypeFieldsRights();
            this.getVersions();
            this.getFields();
        },
        editCondition(item) {
            this.currentItem = item;
            this.conditionModalInstance.edit(item.conditionStr);
        },
        onConditionChanged(condition) {
            this.currentItem.conditionStr = condition.conditionStr;
            this.currentItem.displayConditionStr = condition.displayConditionStr;
        },
        onVersionChange(version) {
            if (this.contentType.id != version.id) {
                this.contentType.id = version.id;
                this.contentType.version = 'v' + version.text;
                this.getContentTypeFieldsRights();
                this.getFields();
            }
        },
        getContentTypeFieldsRights() {
            ContentTypeService.getFieldsRights(this.contentType.id).then((data) => {
                this.fieldsRightsItems = data.map(
                    (dto) => new ContentTypeFieldRight(dto)
                );
            });
        },
        getVersions() {
            ContentTypeService.getVersions(this.contentType.id).then((data) => {
                this.versionList = data;
            });
        },
        getFields() {
            ContentTypeService.getFields([this.contentType.id]).then((data) => {
                this.contentType.fields = data.map(function (dto) {
                    var field = new ContentTypeField(dto);
                    field.id = field.id + '';
                    return field;
                });
                this.loading = false;
            });
        },
        getRoles() {
            RoleService.getList().then((data) => {
                this.rolesList = data;
            });
        },
        saveContentTypeFieldsRights() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }
            var fieldsRights = this.fieldsRightsItems.map((item) => item.toDto());
            ContentTypeService.saveFieldsRights(
                this.contentType.id,
                fieldsRights
            ).then((data) => {
                this.$emit('on-fields-rights-saved', this.fieldsRightsItems);
                this.close();
            });
        },
        addContentTypeFieldsRights() {
            this.fieldsRightsItems.push(new ContentTypeFieldRight());
        },
        removeContentTypeFieldsRights(uuid) {
            this.fieldsRightsItems = this.fieldsRightsItems.filter(
                (item) => item.uuid !== uuid
            );
        },
        close() {
            this.$router.push({
                name: 'ContentTypeList',
            });
        },
    },
};
</script>
<style>
.field-rights-config_header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 5px 0px;
}
</style>